import {
  produce,
  reconcile,
  type SetStoreFunction,
  type Store,
} from "solid-js/store";
import type {
  FileDiff,
  Message,
  PermissionRequest,
  Project,
  Session,
  SessionStatus,
} from "@mux/sdk/v2/client";
import type { State, VcsCache, GlobalStore, SessionDiffSummary } from "./types";
import { trimSessions } from "./session-trim";
import { cmp, getActiveSessionIds, sortSessionsByID } from "./utils";
import { workspaceMatches } from "@/utils/workspace";
import { getActiveGridSessionIDs } from "@/pages/grid/grid-active-sessions";
import { applyMessageEvent } from "./message-event-reducer";
import { applyRequestEvent, applyRequestRouteEvent } from "./request-event-reducer";
import { mergeSessionUpdate } from "./session-merge";
import { admitsSession, removedRevision } from "./session-revision";

export function applyGlobalEvent(input: {
  event: { type: string; properties?: unknown };
  project: Project[];
  setGlobalProject: (next: Project[] | ((draft: Project[]) => void)) => void;
}) {
  if (input.event.type !== "project.updated") return;
  const properties = input.event.properties as Project;
  const index = input.project.findIndex((p) => p.id === properties.id);
  if (index !== -1) {
    input.setGlobalProject((draft) => {
      draft[index] = { ...draft[index], ...properties };
    });
    return;
  }

  const newCreated = properties.time?.created ?? 0;
  const insertIndex = input.project.findIndex(
    (p) => (p.time?.created ?? 0) < newCreated,
  );
  const finalIndex = insertIndex === -1 ? input.project.length : insertIndex;

  input.setGlobalProject((draft) => {
    draft.splice(finalIndex, 0, properties);
  });
}

export function cleanupDroppedSessionCachesGlobal(
  globalStore: Store<GlobalStore>,
  setGlobalStore: SetStoreFunction<GlobalStore>,
  next: Session[],
  directory?: string,
): string[] {
  const keep = new Set(next.map((item) => item.id));
  const scope = directory
    ? new Set(
        globalStore.session
          .filter((session) => workspaceMatches(session, directory))
          .map((item) => item.id),
      )
    : undefined;
  const staleSet = new Set<string>();
  const consider = (sessionID: string | undefined) => {
    if (!sessionID || keep.has(sessionID)) return;
    if (scope && !scope.has(sessionID)) return;
    staleSet.add(sessionID);
  };
  for (const collection of [
    globalStore.message,
    globalStore.session_diff,
    globalStore.session_diff_summary,
    globalStore.permission,
    globalStore.question,
    globalStore.pty_activity ?? {},
  ]) {
    for (const sessionID of Object.keys(collection)) consider(sessionID);
  }
  for (const parts of Object.values(globalStore.part)) {
    for (const part of parts ?? []) {
      if (!part?.sessionID) continue;
      consider(part.sessionID);
      break;
    }
  }
  if (staleSet.size === 0) return [];

  setGlobalStore(
    produce((draft) => {
      for (const key of Object.keys(draft.part)) {
        const parts = draft.part[key];
        if (!parts?.some((part) => staleSet.has(part?.sessionID ?? "")))
          continue;
        delete draft.part[key];
      }
      for (const sessionID of staleSet) {
        delete draft.message[sessionID];
        delete draft.session_diff[sessionID];
        delete draft.session_diff_summary[sessionID];
        delete draft.pty_activity[sessionID];
        delete draft.permission[sessionID];
        delete draft.question[sessionID];
      }
    }),
  );
  return [...staleSet];
}

function cleanupSessionCachesGlobal(
  setGlobalStore: SetStoreFunction<GlobalStore>,
  sessionID: string,
) {
  if (!sessionID) return;
  setGlobalStore(
    produce((draft) => {
      const stale = [sessionID];
      const staleSet = new Set(stale);
      for (const key of Object.keys(draft.part)) {
        const parts = draft.part[key];
        if (!parts?.some((part) => staleSet.has(part?.sessionID ?? "")))
          continue;
        delete draft.part[key];
      }
      delete draft.message[sessionID];
      delete draft.session_diff[sessionID];
      delete draft.session_diff_summary[sessionID];
      delete draft.session_status[sessionID];
      delete draft.session_waiting[sessionID];
      delete draft.pty_activity[sessionID];
      delete draft.permission[sessionID];
      delete draft.question[sessionID];
    }),
  );
}

export function applyDirectoryEvent(input: {
  event: { type: string; properties?: unknown };
  store: Store<State>;
  setStore: SetStoreFunction<State>;
  push: (directory: string) => void;
  directory: string;
  loadLsp: () => void;
  vcsCache?: VcsCache;
}) {
  const event = input.event;
  switch (event.type) {
    case "server.instance.disposed": {
      input.push(input.directory);
      return;
    }
    case "vcs.branch.updated": {
      const props = event.properties as { branch: string };
      if (input.store.vcs?.branch === props.branch) break;
      const next = { branch: props.branch };
      input.setStore("vcs", next);
      if (input.vcsCache) input.vcsCache.setStore("value", next);
      break;
    }
    case "lsp.updated": {
      input.loadLsp();
      break;
    }
  }
}

function trimAndMergeSessions(
  allSessions: Session[],
  dir: string,
  limit: number,
  permission: Record<string, PermissionRequest[]>,
  pinned: Set<string>,
): Session[] {
  const activeSessionIds =
    typeof location === "undefined"
      ? undefined
      : getActiveSessionIds(
          location.pathname,
          location.search,
          getActiveGridSessionIDs(),
          allSessions,
        );
  if (!dir) {
    return trimSessions(allSessions, {
      limit,
      permission,
      pinned,
      activeSessionIds,
    }).sort((a, b) => cmp(a.id, b.id));
  }
  const affected = allSessions.filter((s) => workspaceMatches(s, dir));
  const other = allSessions.filter((s) => !workspaceMatches(s, dir));
  const trimmedAffected = trimSessions(affected, {
    limit,
    permission,
    pinned,
    activeSessionIds,
  });
  return [...trimmedAffected, ...other].sort((a, b) => cmp(a.id, b.id));
}

export function applySessionEvent(input: {
  event: { type: string; properties?: unknown };
  globalStore: Store<GlobalStore>;
  setGlobalStore: SetStoreFunction<GlobalStore>;
  getDirectoryStore: (
    dir: string,
  ) => [Store<State>, SetStoreFunction<State>] | undefined;
  pinned: Set<string>;
  onSessionCachesDropped?: (directory: string, sessionIDs: string[]) => void;
  onTextGap?: Parameters<typeof applyMessageEvent>[0]["onTextGap"];
}) {
  const event = input.event;
  if (applyMessageEvent({ event, store: input.globalStore, setStore: input.setGlobalStore, onTextGap: input.onTextGap })) return;
  if (applyRequestEvent({ event, store: input.globalStore, setStore: input.setGlobalStore })) return;
  if (applyRequestRouteEvent({ event, setStore: input.setGlobalStore })) return;
  switch (event.type) {
    case "session.created": {
      const info = (event.properties as { info: Session }).info;
      if (info.time?.archived) break;
      const sessions = input.globalStore.session || [];
      const index = sessions.findIndex((s) => s.id === info.id);
      if (!admitsSession(input.globalStore, sessions[index], info)) break;
      if (index !== -1) {
        input.setGlobalStore("session", index, reconcile(info));
        break;
      }

      const dir = info.workspaceID || info.directory || "";
      const next = [...sessions, info];
      const merged = trimAndMergeSessions(
        next,
        dir,
        Number.POSITIVE_INFINITY,
        input.globalStore.permission,
        input.pinned,
      );

      const dropped = cleanupDroppedSessionCachesGlobal(
        input.globalStore,
        input.setGlobalStore,
        merged,
        dir,
      );
      if (dir && dropped.length > 0) input.onSessionCachesDropped?.(dir, dropped);
      input.setGlobalStore("session", reconcile(merged, { key: "id" }));

      if (!info.parentID && dir) {
        input.setGlobalStore("sessionTotal", dir, (value) => (value ?? 0) + 1);
      }
      break;
    }

    case "session.updated": {
      const info = (event.properties as { info: Session }).info;
      const sessions = input.globalStore.session || [];
      const index = sessions.findIndex((s) => s.id === info.id);
      if (!admitsSession(input.globalStore, sessions[index], info)) break;
      const dir = info.workspaceID || info.directory || "";

      if (index !== -1) {
        const existing = sessions[index];
        const previousDir = existing.workspaceID || existing.directory || "";
        const wasArchived = !!existing.time?.archived;
        const isArchived = !!info.time?.archived;
        const moved = !!previousDir && !!dir && previousDir !== dir;
        if (!existing.parentID && previousDir && !wasArchived && (isArchived || moved)) {
          input.setGlobalStore("sessionTotal", previousDir, (value) => Math.max(0, (value ?? 0) - 1));
        }
        if (!existing.parentID && dir && !isArchived && (wasArchived || moved)) {
          input.setGlobalStore("sessionTotal", dir, (value) => (value ?? 0) + 1);
        }
        const updated = mergeSessionUpdate(existing, info);

        // Session IDs are immutable, so update only this keyed row. Replacing and
        // reconciling the full array invalidates every sidebar list for a one-row change.
        input.setGlobalStore("session", index, reconcile(updated));
        break;
      }
      if (info.time?.archived) {
        input.setGlobalStore("session", reconcile(sortSessionsByID([...sessions, info]), { key: "id" }));
        break;
      }
      const next = [...sessions, info];
      const merged = trimAndMergeSessions(
        next,
        dir,
        Number.POSITIVE_INFINITY,
        input.globalStore.permission,
        input.pinned,
      );
      const dropped = cleanupDroppedSessionCachesGlobal(
        input.globalStore,
        input.setGlobalStore,
        merged,
        dir,
      );
      if (dir && dropped.length > 0) input.onSessionCachesDropped?.(dir, dropped);
      input.setGlobalStore("session", reconcile(merged, { key: "id" }));
      break;
    }

    case "session.deleted": {
      const info = (event.properties as { info: Session }).info;
      const sessions = input.globalStore.session || [];
      const index = sessions.findIndex((s) => s.id === info.id);
      const dir = info.workspaceID || info.directory || "";
      input.setGlobalStore("session_removed", info.id, removedRevision(input.globalStore, info.id, info, sessions[index]));
      if (index !== -1) {
        const existing = sessions[index];
        input.setGlobalStore(
          "session",
          produce((draft) => {
            draft.splice(index, 1);
          }),
        );
        if (!existing.parentID && !existing.time?.archived && dir) {
          input.setGlobalStore("sessionTotal", dir, (value) =>
            Math.max(0, (value ?? 0) - 1),
          );
        }
      }
      cleanupSessionCachesGlobal(input.setGlobalStore, info.id);
      break;
    }

    case "session.diff.updated": {
      const props = event.properties as {
        sessionID: string;
        summary: SessionDiffSummary;
      };
      input.setGlobalStore(
        "session_diff_summary",
        props.sessionID,
        reconcile(props.summary),
      );
      break;
    }

    case "session.diff": {
      const props = event.properties as { sessionID: string; diff: FileDiff[] };
      input.setGlobalStore(
        "session_diff",
        props.sessionID,
        reconcile(props.diff, { key: "file" }),
      );
      break;
    }

    case "session.compacted": {
      const props = event.properties as { sessionID: string; contextTokens?: number };
      if (!(props.contextTokens && props.contextTokens > 0)) break;
      const messages = input.globalStore.message[props.sessionID];
      if (!messages) break;
      const index = messages.findLastIndex((message) => message.role === "assistant" && message.summary === true);
      if (index < 0) break;
      input.setGlobalStore(
        "message",
        props.sessionID,
        index,
        reconcile({
          ...messages[index],
          tokens: { input: props.contextTokens, output: 0, reasoning: 0, cache: { read: 0, write: 0 } },
        } as Message),
      );
      break;
    }

    case "session.idle":
    case "session.error": {
      const props = event.properties as { sessionID: string };
      input.setGlobalStore("session_status", props.sessionID, reconcile({ type: "idle" }));
      break;
    }

    case "session.status": {
      const props = event.properties as {
        sessionID: string;
        status: SessionStatus;
      };
      input.setGlobalStore(
        "session_status",
        props.sessionID,
        reconcile(props.status),
      );
      break;
    }

    case "pty.created": {
      const info = (
        event.properties as {
          info?: { id?: string; sessionID?: string; status?: string };
        }
      ).info;
      if (!info?.sessionID || !info.id) break;
      input.setGlobalStore("pty_activity", info.sessionID, {
        alive: info.status !== "exited",
        active: false,
        ptyID: info.id,
        updatedAt: Date.now(),
      });
      break;
    }

    case "pty.activity": {
      const props = event.properties as {
        id?: string;
        sessionID?: string;
        active?: boolean;
        alive?: boolean;
      };
      if (!props.sessionID) break;
      const prev = input.globalStore.pty_activity[props.sessionID];
      // A replaced PTY's final events can arrive after its successor's; they
      // must not mark the session's current terminal dead.
      if (props.alive === false && prev?.ptyID && props.id && prev.ptyID !== props.id) break;
      input.setGlobalStore("pty_activity", props.sessionID, {
        alive: props.alive ?? prev?.alive ?? true,
        active: props.active ?? false,
        ptyID: props.id ?? prev?.ptyID,
        updatedAt: Date.now(),
      });
      break;
    }

    case "pty.exited":
    case "pty.deleted": {
      const props = event.properties as { id?: string; sessionID?: string };
      const sessionID =
        props.sessionID ||
        Object.entries(input.globalStore.pty_activity).find(
          ([, value]) => value?.ptyID === props.id,
        )?.[0];
      if (!sessionID) break;
      const prev = input.globalStore.pty_activity[sessionID];
      if (prev?.ptyID && props.id && prev.ptyID !== props.id) break;
      input.setGlobalStore("pty_activity", sessionID, {
        alive: false,
        active: false,
        ptyID: props.id,
        updatedAt: Date.now(),
      });
      break;
    }

  }
}
